import moment from "moment";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import AppButton from "../../../../../components/common/AppButton";
import AppDialog from "../../../../../components/common/AppDialog";
import { SVG } from "../../../../../constants/assetLists";
import SummaryCard, { SummaryCardData } from "./components/SummaryCard";
import {
  handleAcknowledgeDaySummaryService,
  handleGetDaySummaryService,
} from "../../../../../services/operations/summary";
import QUERY_KEYS from "../../../../../constants/queryKeys";
import { DaySummaryRow, DaySummaryRows } from "./types";
import { useSnackbar } from "../../../../../context/SnackbarContextProvider";
import { useRef, useState } from "react";

type Props = {
  showModal: boolean;
  onClose: () => void;
};

const getCardsGridClasses = (count: number) =>
  count === 1
    ? "grid-cols-1"
    : count === 2
      ? "grid-cols-2"
      : "grid-cols-1 lg:grid-cols-3";

const getGreeting = () => {
  const now = moment();
  const minutesOfDay = now.hours() * 60 + now.minutes();

  if (minutesOfDay < 5 * 60 || minutesOfDay > 21 * 60) return "לילה טוב"; // 21:01 - 04:59
  if (minutesOfDay < 12 * 60) return "בוקר טוב"; // 05:00 - 11:59
  if (minutesOfDay <= 15 * 60) return "צהריים טובים"; // 12:00 - 15:00
  if (minutesOfDay <= 18 * 60) return "אחר צהריים טובים"; // 15:01 - 18:00

  return "ערב טוב"; // 18:01 - 21:00
};

const toSummaryCardData = (row: DaySummaryRow): SummaryCardData => ({
  id: row._id,
  name: row.name,
  logo: row.logo,
  level: row.level,
  clientId: row.clientId,
  branchId: row.branchId,
  overDueCount: row.overDueCount,
  currentCount: row.currentCount,
  comingSoonCount: row.comingSoonCount,
  overDue: row.overDue,
  currentState: row.currentState,
  comingSoon: row.comingSoon,
});

const getSummaryGroupKey = (group: SummaryCardData[], groupIndex: number) =>
  `${group[0]?.clientId ?? group[0]?.id ?? groupIndex}-${groupIndex}`;

const getCurrentGreeting = () => {
  const now = moment();
  const minutesFromMidnight = now.hours() * 60 + now.minutes();

  if (minutesFromMidnight >= 5 * 60 && minutesFromMidnight < 12 * 60) {
    return "בוקר טוב";
  }

  if (minutesFromMidnight >= 12 * 60 && minutesFromMidnight <= 15 * 60) {
    return "צהריים טובים";
  }

  if (minutesFromMidnight > 15 * 60 && minutesFromMidnight <= 18 * 60) {
    return "אחר צהריים טובים";
  }

  if (minutesFromMidnight > 18 * 60 && minutesFromMidnight <= 21 * 60) {
    return "ערב טוב";
  }

  return "לילה טוב";
};

function DaySummary({ showModal, onClose }: Props) {
  const [openNetworkGroups, setOpenNetworkGroups] = useState<
    Record<string, boolean>
  >({});
  const networkGroupRefs = useRef<Record<string, HTMLElement | null>>({});
  const todayLabel = moment().locale("he").format("dddd ה-DD/MM/YYYY");
  const greeting = getGreeting();
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();
  const stringifyUser = localStorage.getItem("user");
  const user =
    stringifyUser && stringifyUser !== "undefined"
      ? JSON.parse(stringifyUser)
      : null;

  const { data } = useQuery({
    queryKey: [QUERY_KEYS.GET_DAY_SUMMARY],
    queryFn: handleGetDaySummaryService,
    enabled: showModal,
  });

  const acknowledgeSummaryMutation = useMutation({
    mutationKey: [QUERY_KEYS.ACKNOWLEDGE_DAY_SUMMARY],
    mutationFn: handleAcknowledgeDaySummaryService,
    onSuccess: (response) => {
      queryClient.setQueryData([QUERY_KEYS.GET_DAY_SUMMARY_STATUS], response);
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_DAY_SUMMARY],
      });
      onClose();
      showSnackbar({
        message: response?.message ?? "הסיכום אושר בהצלחה",
        type: "success",
      });
    },
    onError: (error: any) => {
      showSnackbar({
        message: error?.message || "אישור הסיכום נכשל, נסה שוב.",
        type: "error",
      });
    },
  });

  const rowGroups: DaySummaryRows = data?.data?.rows ?? [];
  const summaryGroups: SummaryCardData[][] = rowGroups.map((group) =>
    group.map(toSummaryCardData),
  );

  const handleApprove = () => {
    acknowledgeSummaryMutation.mutate();
  };

  const toggleNetworkGroup = (groupKey: string) => {
    const shouldOpen = !(openNetworkGroups[groupKey] ?? true);

    setOpenNetworkGroups((current) => ({
      ...current,
      [groupKey]: !(current[groupKey] ?? true),
    }));

    if (shouldOpen) {
      requestAnimationFrame(() => {
        networkGroupRefs.current[groupKey]?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      });
    }
  };

  return (
    <AppDialog
      showModel={showModal}
      title="סיכום היום"
      titleIcon={
        <img
          src={SVG.darkStarIcon}
          alt="icon"
          className="w-[20px] h-[20px] object-contain"
        />
      }
      onClose={onClose}
      showHeaderLine={false}
      width={1200}
      fullHeight
      mainContentClasses="!px-0"
    >
      <div className="w-full h-full flex flex-col gap-4">
        <div className="px-3 flex-1 min-h-0 overflow-y-auto flex flex-col gap-4">
          <div className="mt-2 flex flex-col items-center">
            <div className="w-[140px] h-[24px]">
              <img
                src={SVG.logoDarkIcon}
                alt="logo"
                className="w-full h-full object-contain"
              />
            </div>
            <p className="m-0 text-xl text-deepAqua font-bold text-center">
              {greeting} {user?.firstName},
            </p>
            <p className="m-0 text-xl text-darkerGray font-medium text-center">
              {`סיכום יום ${todayLabel}`}
            </p>
          </div>

          <div className="flex flex-col gap-3">
            {summaryGroups.map((group, groupIndex) => {
              const groupKey = getSummaryGroupKey(group, groupIndex);
              const isNetworkOpen = openNetworkGroups[groupKey] ?? true;

              return (
                <section
                  key={groupKey}
                  ref={(element) => {
                    networkGroupRefs.current[groupKey] = element;
                  }}
                  className="rounded-[10px] bg-[#F3F3F3] p-[10px]"
                >
                  <div
                    className={`flex items-center gap-2.5 ${isNetworkOpen ? "pb-3" : ""}`}
                  >
                    <button
                      className="w-[14px] h-[14px]"
                      onClick={() => toggleNetworkGroup(groupKey)}
                    >
                      <img
                        src={isNetworkOpen ? SVG.expandIcon : SVG.lessThanIcon}
                        alt="toggle filter"
                        className="w-full h-full object-contain"
                      />
                    </button>

                    <span className="text-lg font-bold text-darkerGray">
                      {group[0]?.name}
                    </span>
                  </div>
                  {isNetworkOpen && (
                    <div
                      className={`grid gap-3 items-start ${getCardsGridClasses(group.length)}`}
                    >
                      {group.map((summary: SummaryCardData) => (
                        <SummaryCard
                          key={summary.id}
                          data={summary}
                          onClose={onClose}
                        />
                      ))}
                    </div>
                  )}
                </section>
              );
            })}
          </div>
        </div>

        <div className="flex justify-end pt-4 px-3 border-t border-t-gray70">
          <AppButton
            variant="primary"
            onClick={handleApprove}
            isLoading={acknowledgeSummaryMutation.isPending}
            disabled={data?.data?.delivery?.acknowledgedAt !== null}
          >
            אישור
          </AppButton>
        </div>
      </div>
    </AppDialog>
  );
}

export default DaySummary;
